import "./styles/App.css";
import "./styles/shared.css";
import "./styles/theme.css"; // imported LAST so token-based rules override legacy table colors
import { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/css/bootstrap.min.css";

import { useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import Layout from "./components/layout/Layout";
import LoginForm from "./components/auth/LoginForm";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Notes from "./pages/Notes";
import Deals from "./pages/Deals";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import OTPModal from "./components/modals/OTPModal";
import UserInfoModal from "./components/modals/UserInfoModal";
import Loader from "./components/common/Loader";
import ConfirmHost from "./components/common/ConfirmDialog";

// Routes a non-admin is allowed to land on. Anything else bounces to /dashboard.
const USER_ALLOWED = new Set(["dashboard", "notes", "settings"]);

const RequireAdmin = ({ role, children }) => {
  if (role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const { isLoggedIn, isLoading, role, userInfo, login } = useAuth();
  const { theme } = useTheme();
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpLogin, setOtpLogin] = useState(null);
  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    login(email, setOtpLogin, setShowOTPModal);
  };

  const handleCancelOTP = () => {
    try {
      if (otpLogin) otpLogin.emit("cancel");
      setShowOTPModal(false);
      setOtpLogin(null);
    } catch (err) {
      console.error("Error canceling OTP login:", err);
    }
  };

  const toggleUserInfoModal = () => setIsUserInfoModalOpen((v) => !v);

  if (isLoading) return <Loader />;

  return (
    <div className="App" data-theme={theme}>
      <ToastContainer position="top-right" theme={theme} />
      {!isLoggedIn ? (
        <LoginForm
          email={email}
          handleLoginSubmit={handleLoginSubmit}
          setEmail={setEmail}
        />
      ) : (
        <Routes>
          <Route path="/" element={<Layout toggleUserInfoModal={toggleUserInfoModal} />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="notes" element={<Notes />} />
            <Route path="settings" element={<Settings />} />
            <Route path="deals" element={<RequireAdmin role={role}><Deals /></RequireAdmin>} />
            <Route path="reports" element={<RequireAdmin role={role}><Reports /></RequireAdmin>} />
            <Route path="organization" element={<RequireAdmin role={role}><Admin /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      )}

      {showOTPModal && (
        <OTPModal login={otpLogin} handleCancel={handleCancelOTP} />
      )}

      <UserInfoModal
        isOpen={isUserInfoModalOpen}
        toggle={toggleUserInfoModal}
        userInfo={userInfo}
      />

      <ConfirmHost />
    </div>
  );
}

export default App;
